import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Play } from 'lucide-react';
import { isUploadedFile, toEmbedUrl } from '../lib/landingAssets';
import { HERO_TOOLS } from '../constants';

const SlideMedia = ({ slide }) => {
  if (slide?.type === 'video' && isUploadedFile(slide.url)) {
    return <video src={slide.url} poster={slide.thumbnail} autoPlay muted loop playsInline className="w-full h-full object-cover" data-testid="hero-banner-video" />;
  }
  if (slide?.type === 'video' || slide?.type === 'url') {
    return (
      <iframe
        src={toEmbedUrl(slide.external_url || slide.url)}
        title={slide.title || 'hero'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
        data-testid="hero-banner-embed"
      />
    );
  }
  return <img src={slide?.url} alt={slide?.title || ''} className="w-full h-full object-cover" data-testid="hero-banner-image" />;
};

const HeroWindow = ({ slides, active, setActive, onOpenDemo }) => {
  const current = slides[active];
  return (
    <div className="rounded-2xl border border-white/15 bg-[#0d0a18]/70 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.55)] p-3 grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3" data-testid="hero-banner">
      <ul className="hidden sm:block space-y-2">
        {HERO_TOOLS.map(({ title, desc, icon: Icon }, i) => (
          <li key={title} className={`flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 border transition-colors ${i === 0 ? 'border-violet-400/70 bg-violet-500/15 shadow-[0_0_20px_rgba(139,92,246,0.35)]' : 'border-white/5 bg-white/[0.03] hover:bg-white/[0.07]'}`}>
            <span className="w-8 h-8 shrink-0 rounded-lg bg-white/10 flex items-center justify-center"><Icon size={15} /></span>
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold truncate">{title}</span>
              <span className="block text-[11px] text-white/50 truncate">{desc}</span>
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-3 min-w-0">
        <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="absolute inset-0">
              <SlideMedia slide={current} />
            </motion.div>
          </AnimatePresence>
          {current?.type === 'image' && (
            <button onClick={onOpenDemo} className="absolute inset-0 flex items-center justify-center group" aria-label="Watch demo" data-testid="hero-banner-play-btn">
              <span className="w-14 h-14 rounded-full bg-white/15 backdrop-blur border border-white/30 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-1 w-6 h-6 fill-white text-white" />
              </span>
            </button>
          )}
          {current?.title && <span className="absolute left-3 bottom-3 text-xs font-semibold bg-black/50 backdrop-blur px-2.5 py-1 rounded-full pointer-events-none">{current.title}</span>}
        </div>
        {slides.length > 1 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {slides.map((s, idx) => (
              <button
                key={idx}
                onClick={() => setActive(idx)}
                className={`relative h-12 w-16 shrink-0 rounded-lg overflow-hidden border-2 transition-[border-color,opacity] ${active === idx ? 'border-violet-400' : 'border-transparent opacity-60 hover:opacity-100'}`}
                aria-label={`Show slide ${idx + 1}`}
                data-testid={`hero-slide-indicator-${idx}`}
              >
                {s.type === 'image' || s.thumbnail ? (
                  <img src={s.type === 'image' ? s.url : s.thumbnail} alt="" className="w-full h-full object-cover" />
                ) : s.type === 'video' && isUploadedFile(s.url) ? (
                  <video src={`${s.url}#t=0.5`} preload="metadata" muted playsInline className="w-full h-full object-cover pointer-events-none" />
                ) : (
                  <span className="w-full h-full bg-violet-900/60 flex items-center justify-center"><Play size={14} className="fill-white" /></span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroWindow;
